// TeacherAddLesson.js
import React, { useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Select,
  MenuItem,
  LinearProgress,
  Typography,
  Stepper,
  Step,
  StepLabel, 
  Snackbar, 
  Alert
} from '@mui/material';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import FilePresentIcon from '@mui/icons-material/FilePresent';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import ImageIcon from '@mui/icons-material/Image';
import LinkIcon from '@mui/icons-material/Link';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import AnimationIcon from '@mui/icons-material/Animation';
import { useTranslation } from "react-i18next";
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { collection, doc, setDoc } from 'firebase/firestore';
import { storage, db } from '../firebaseConfig';

function TeacherAddLesson({ groupId, categories, fetchFiles, onClose }) {
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState(0);
  const [categorySelection, setCategorySelection] = useState('');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newFile, setNewFile] = useState(null);
  const [newFileType, setNewFileType] = useState(null);
  const [video, setVideo] = useState(null);
  const [audio, setAudio] = useState(null);
  const [image, setImage] = useState(null);
  const [swf, setSwf] = useState(null);
  const [linkName, setLinkName] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [progress, setProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const uploadFile = async () => {
    setError('');

    if (!categorySelection) {
      setError(t("Please choose a category."));
      return;
    }

    const category =
      categorySelection === 'create-new' ? newCategoryName : categorySelection || t("Uncategorized");

    if (categorySelection === 'create-new' && !newCategoryName) {
      setError(t("Please enter a new category name."));
      return;
    }

    const generateUniqueFileName = async (fileName, groupId) => {
      const basePath = `groups/${groupId}/`;
      const [name, ext] = fileName.split(/(?=\.[^.]+$)/);
      let uniqueName = fileName;
      let version = 1;
      let fileExists = true;

      while (fileExists) {
        const fileRef = ref(storage, `${basePath}${uniqueName}`);

        try {
          await getDownloadURL(fileRef);
          version++;
          uniqueName = `${name}_v${version}${ext}`;
        } catch (error) {
          fileExists = false;
        }
      }

      return uniqueName;
    };

    if (newFileType === 'file') {
      try {
        setIsUploading(true);

        if (!newFile || newFile.type !== 'application/pdf' || newFile.size > 20 * 1024 * 1024) {
          setError(t("Only PDF files up to 20 MB are accepted."));
          return;
        }

        const uniqueFileName = await generateUniqueFileName(newFile.name, groupId);

        const storageRef = ref(storage, `groups/${groupId}/${uniqueFileName}`);
        const uploadTask = uploadBytesResumable(storageRef, newFile);

        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
            setProgress(progress);
          },
          (error) => {
            console.error('Error uploading file:', error);
            setProgress(0);
            setIsUploading(false);
          },
          async () => {
            const fileURL = await getDownloadURL(uploadTask.snapshot.ref);

            const groupRef = doc(db, 'groups', groupId);
            const filesCollection = collection(groupRef, 'files');
            await setDoc(doc(filesCollection), {
              name: uniqueFileName,
              originalName: newFile.name,
              url: fileURL,
              markedImportant: false,
              category,
              uploadedAt: new Date(),
              type: newFileType,
            });

            fetchFiles();
            onClose();
            setCategorySelection('');
            setNewCategoryName('');
            setNewFile(null);
            setProgress(0);
            setIsUploading(false);
          }
        );
      } catch (error) {
        console.error('Error during file upload process:', error);
        setIsUploading(false);
      }

  } else if (newFileType === 'video') {
      try {
        setIsUploading(true);
        
        // Optional: validate video size or type if needed
        if (!video) {
          setError(t("Please choose a video."));
          return;
        }
    
        // Generate a unique name for the video in Storage
        const uniqueFileName = await generateUniqueFileName(video.name, groupId);
        const storageRef = ref(storage, `groups/${groupId}/${uniqueFileName}`);
    
        // Use Firebase’s uploadBytesResumable for the video
        const uploadTask = uploadBytesResumable(storageRef, video);
      
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progressValue = Math.round(
              (snapshot.bytesTransferred / snapshot.totalBytes) * 100
            );
            setProgress(progressValue);
          },
          (error) => {
            console.error('Error uploading video:', error);
            setProgress(0);
            setIsUploading(false);
          },
          async () => {
            // Once upload completes, get its download URL
            const fileURL = await getDownloadURL(uploadTask.snapshot.ref);
    
            // Save the video info to Firestore
            const groupRef = doc(db, 'groups', groupId);
            const filesCollection = collection(groupRef, 'files');
            await setDoc(doc(filesCollection), {
              name: uniqueFileName,
              originalName: video.name,
              url: fileURL,
              markedImportant: false,
              category,
              uploadedAt: new Date(),
              type: 'video',
            });
    
            // Refresh UI and close the dialog
            fetchFiles();
            onClose();
            setCategorySelection('');
            setNewCategoryName('');
            setVideo(null);
            setProgress(0);
            setIsUploading(false);
          }
        );
      } catch (error) {
        console.error('Error during video upload process:', error);
        setIsUploading(false);
      }
    } else if (newFileType === 'audio') {
      try {
        setIsUploading(true);

        if (!audio) {
          setError(t("Please choose an audio file."));
          setIsUploading(false);
          return;
        }

        if (audio.size > 100 * 1024 * 1024) {
          setError(t("The audio file must be under 100 MB."));
          setIsUploading(false);
          return;
        }

        const uniqueFileName = await generateUniqueFileName(audio.name, groupId);
        const storageRef = ref(storage, `groups/${groupId}/${uniqueFileName}`);
        const uploadTask = uploadBytesResumable(storageRef, audio);

        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progressValue = Math.round(
              (snapshot.bytesTransferred / snapshot.totalBytes) * 100
            );
            setProgress(progressValue);
          },
          (error) => {
            console.error('Error uploading audio:', error);
            setProgress(0);
            setIsUploading(false);
          },
          async () => {
            const fileURL = await getDownloadURL(uploadTask.snapshot.ref);

            const groupRef = doc(db, 'groups', groupId);
            const filesCollection = collection(groupRef, 'files');
            await setDoc(doc(filesCollection), {
              name: uniqueFileName,
              originalName: audio.name,
              url: fileURL,
              markedImportant: false,
              category,
              uploadedAt: new Date(),
              type: 'audio',
            });

            fetchFiles();
            onClose();
            setCategorySelection('');
            setNewCategoryName('');
            setAudio(null);
            setProgress(0);
            setIsUploading(false);
          }
        );
      } catch (error) {
        console.error('Error during audio upload process:', error);
        setIsUploading(false);
      }
    } else if (newFileType === 'swf') {
      try {
        setIsUploading(true);

        if (!swf || !/\.swf$/i.test(swf.name)) {
          setError(t("Please choose an SWF file."));
          setIsUploading(false);
          return;
        }

        if (swf.size > 50 * 1024 * 1024) {
          setError(t("The SWF file must be under 50 MB."));
          setIsUploading(false);
          return;
        }

        const uniqueFileName = await generateUniqueFileName(swf.name, groupId);
        const storageRef = ref(storage, `groups/${groupId}/${uniqueFileName}`);
        const uploadTask = uploadBytesResumable(storageRef, swf, {
          contentType: 'application/x-shockwave-flash',
        });

        uploadTask.on(
          'state_changed',
          (snapshot) => {
            setProgress(Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100));
          },
          (error) => {
            console.error('Error uploading SWF:', error);
            setProgress(0);
            setIsUploading(false);
          },
          async () => {
            const fileURL = await getDownloadURL(uploadTask.snapshot.ref);

            const groupRef = doc(db, 'groups', groupId);
            const filesCollection = collection(groupRef, 'files');
            await setDoc(doc(filesCollection), {
              name: uniqueFileName,
              originalName: swf.name,
              url: fileURL,
              markedImportant: false,
              category,
              uploadedAt: new Date(),
              type: 'swf',
            });

            fetchFiles();
            onClose();
            setCategorySelection('');
            setNewCategoryName('');
            setSwf(null);
            setProgress(0);
            setIsUploading(false);
          }
        );
      } catch (error) {
        console.error('Error during SWF upload process:', error);
        setIsUploading(false);
      }
    } else if (newFileType === 'image') {
      try {
        setIsUploading(true);

        if (!image) {
          setError(t("Please choose an image."));
          setIsUploading(false);
          return;
        }

        if (image.size > 20 * 1024 * 1024) {
          setError(t("The image must be under 20 MB."));
          setIsUploading(false);
          return;
        }

        const uniqueFileName = await generateUniqueFileName(image.name, groupId);
        const storageRef = ref(storage, `groups/${groupId}/${uniqueFileName}`);
        const uploadTask = uploadBytesResumable(storageRef, image);

        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progressValue = Math.round(
              (snapshot.bytesTransferred / snapshot.totalBytes) * 100
            );
            setProgress(progressValue);
          },
          (error) => {
            console.error('Error uploading image:', error);
            setProgress(0);
            setIsUploading(false);
          },
          async () => {
            const fileURL = await getDownloadURL(uploadTask.snapshot.ref);

            const groupRef = doc(db, 'groups', groupId);
            const filesCollection = collection(groupRef, 'files');
            await setDoc(doc(filesCollection), {
              name: uniqueFileName,
              originalName: image.name,
              url: fileURL,
              markedImportant: false,
              category,
              uploadedAt: new Date(),
              type: 'image',
            });

            fetchFiles();
            onClose();
            setCategorySelection('');
            setNewCategoryName('');
            setImage(null);
            setProgress(0);
            setIsUploading(false);
          }
        );
      } catch (error) {
        console.error('Error during image upload process:', error);
        setIsUploading(false);
      }
    } else if (newFileType === 'link') {
      try {
        setIsUploading(true);

        if (!linkName.trim()) {
          setError(t("Please enter a link name."));
          setIsUploading(false);
          return;
        }

        if (!linkUrl.trim()) {
          setError(t("Please enter a valid link."));
          setIsUploading(false);
          return;
        }

        const groupRef = doc(db, 'groups', groupId);
        const filesCollection = collection(groupRef, 'files');

        await setDoc(doc(filesCollection), {
          name: linkName.trim(),       // اسم الرابط
          url: linkUrl.trim(),         // رابط فقط
          markedImportant: false,
          category,
          uploadedAt: new Date(),
          type: 'link',
        });

        fetchFiles();
        onClose();
        setCategorySelection('');
        setNewCategoryName('');
        setNewFile(null);
        setVideo(null);
        setAudio(null);
        setImage(null);
        setSwf(null);
        setLinkUrl('');
        setLinkName('');
        setNewFileType(null);
        setProgress(0);
        setIsUploading(false);
      } catch (error) {
        console.error('Error saving link:', error);
        setIsUploading(false);
      }
    }
  };

  const handleNext = () => {
    if (activeStep === 0) {
      if (!categorySelection) {
        setError(t("Please choose a category."));
        return;
      }

      if (categorySelection === 'create-new' && !newCategoryName) {
        setError(t("Please enter a new category name."));
        return;
      }
    }

    setError('');
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setError('');
    setActiveStep((prevStep) => prevStep - 1);
  };

  const startLiveMeeting = () => {
    const liveUrl = `/teacher/class/${groupId}/live`;
    window.open(liveUrl, '_blank', 'noopener,noreferrer');
  };


  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return; 
    }
    setSnackbarOpen(false);
  };

  return (
    <div>
      <Dialog
        open
        onClose={() => {
          setLinkUrl('');
          setLinkName('');
          setNewFileType(null);
          setAudio(null);
          setImage(null);
          setSwf(null);
          onClose();
        }}
        sx={{
          '& .MuiDialog-paper': {
            minWidth: '45%',
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(15, 23, 42, 0.18)',
          },
        }}
      >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'center', fontSize: '20px', textAlign: 'center' }}>
        <b>{t("Add file to category")}</b>
      </DialogTitle>

      <DialogContent
        sx={{
          "& .MuiOutlinedInput-root": { borderRadius: 2 },
          "& .MuiInputBase-root": { borderRadius: 2 },
        }}
      >
        <Stepper activeStep={activeStep} alternativeLabel sx={{marginBottom: '30px'}}>
          <Step>
            <StepLabel>{t("Choose category")}</StepLabel>
          </Step>
          <Step>
            <StepLabel>{t("Add file/video")}</StepLabel>
          </Step>
        </Stepper>

        {activeStep === 0 && (
          <>
            <Select
              fullWidth
              value={categorySelection}
              onChange={(e) => setCategorySelection(e.target.value)}
              displayEmpty
              style={{ marginBottom: '10px', direction: 'rtl' }}
              >
              
              <MenuItem
                value="create-new"
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  fontSize: '20px',
                  color: 'success.main',
                  border: '1px solid green',
                }}
              >
                <PlaylistAddIcon sx={{marginRight: '5px'}}/> {t("Create new category")}
              </MenuItem>

              <MenuItem value="" sx={{direction: 'rtl'}} disabled>
                {t("Please choose or create a category")}
              </MenuItem>

              {categories.map(([category]) => (
                <MenuItem key={category} value={category} sx={{direction: 'rtl'}}>
                  {category}
                </MenuItem>
              ))}
            </Select>
            
            {categorySelection === 'create-new' && (
              <TextField
                fullWidth
                variant="outlined"
                margin="dense"
                label={t("New category name")}
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
              />
            )}
          </>
        )}
        {activeStep === 1 && (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                margin: '20px 0',
                gap: '10px',
              }}
            >
              {/* Add File Ticket */}
              <div
                onClick={() => document.getElementById('file-input').click()}
                style={{
                  cursor: 'pointer',
                  textAlign: 'center',
                  padding: '20px',
                  border: '0.5px solid Black',
                  borderRadius: '8px',
                  transition: '0.3s',
                }}
              >
                <Typography variant="h6" sx={{ marginBottom: '10px' }}>
                  {t("Add file")}
                </Typography>
                <Typography variant="body2">
                  <FilePresentIcon sx={{ fontSize: '70px' }} />
                </Typography>
              </div>
              <input
                id="file-input"
                type="file"
                accept="application/pdf"
                onChange={(e) => {
                  setNewFileType('file');
                  setNewFile(e.target.files[0]);
                }}
                style={{ display: 'none' }}
              />

              {/* Add Link Ticket */}
              <div
                onClick={() => {
                  setNewFileType('link');
                  setNewFile(null);
                  setVideo(null);
                  setAudio(null);
                  setImage(null);
                  setSwf(null);
                }}
                style={{
                  cursor: 'pointer',
                  textAlign: 'center',
                  padding: '20px',
                  border: '0.5px solid Black',
                  borderRadius: '8px',
                  transition: '0.3s',
                }}
              >
                <Typography variant="h6" sx={{ marginBottom: '10px' }}>
                  {t("Add link")}
                </Typography>
                <Typography variant="body2">
                  <LinkIcon sx={{ fontSize: '70px' }} />
                </Typography>
              </div>

              {/* Add Video Ticket */}
              <div
                onClick={() => document.getElementById('video-input').click()}
                style={{
                  cursor: 'pointer',
                  textAlign: 'center',
                  padding: '20px',
                  border: '0.5px solid Black',
                  borderRadius: '8px',
                  transition: '0.3s',
                }}
              >
                <Typography variant="h6" sx={{ marginBottom: '10px' }}>
                  {t("Add video")}
                </Typography>
                <Typography variant="body2">
                  <PlayCircleOutlineIcon sx={{ fontSize: '70px' }} />
                </Typography>
              </div>

              {/* Hidden input for selecting video */}
              <input
                id="video-input"
                type="file"
                accept="video/*"
                onChange={(e) => {
                  setNewFileType('video');
                  setVideo(e.target.files[0]);
                }}
                style={{ display: 'none' }}
              />

              {/* Add Audio Ticket */}
              <div
                onClick={() => document.getElementById('audio-input').click()}
                style={{
                  cursor: 'pointer',
                  textAlign: 'center',
                  padding: '20px',
                  border: '0.5px solid Black',
                  borderRadius: '8px',
                  transition: '0.3s',
                }}
              >
                <Typography variant="h6" sx={{ marginBottom: '10px' }}>
                  {t("Add audio")}
                </Typography>
                <Typography variant="body2">
                  <AudiotrackIcon sx={{ fontSize: '70px' }} />
                </Typography>
              </div>

              {/* Hidden input for selecting audio */}
              <input
                id="audio-input"
                type="file"
                accept="audio/*"
                onChange={(e) => {
                  setNewFileType('audio');
                  setAudio(e.target.files[0]);
                }}
                style={{ display: 'none' }}
              />

              {/* Add Image Ticket */}
              <div
                onClick={() => document.getElementById('image-input').click()}
                style={{
                  cursor: 'pointer',
                  textAlign: 'center',
                  padding: '20px',
                  border: '0.5px solid Black',
                  borderRadius: '8px',
                  transition: '0.3s',
                }}
              >
                <Typography variant="h6" sx={{ marginBottom: '10px' }}>
                  {t("Add image")}
                </Typography>
                <Typography variant="body2">
                  <ImageIcon sx={{ fontSize: '70px' }} />
                </Typography>
              </div>

              {/* Hidden input for selecting image */}
              <input
                id="image-input"
                type="file"
                accept="image/*"
                onChange={(e) => {
                  setNewFileType('image');
                  setImage(e.target.files[0]);
                }}
                style={{ display: 'none' }}
              />

              {/* Add Flash animation (SWF) Ticket */}
              <div
                onClick={() => document.getElementById('swf-input').click()}
                style={{
                  cursor: 'pointer',
                  textAlign: 'center',
                  padding: '20px',
                  border: '0.5px solid Black',
                  borderRadius: '8px',
                  transition: '0.3s',
                }}
              >
                <Typography variant="h6" sx={{ marginBottom: '10px' }}>
                  {t("Add Flash animation (SWF)")}
                </Typography>
                <Typography variant="body2">
                  <AnimationIcon sx={{ fontSize: '70px' }} />
                </Typography>
              </div>

              {/* Hidden input for selecting a SWF animation */}
              <input
                id="swf-input"
                type="file"
                accept=".swf,application/x-shockwave-flash"
                onChange={(e) => {
                  setNewFileType('swf');
                  setSwf(e.target.files[0]);
                }}
                style={{ display: 'none' }}
              />
            </div>

            {/* 🔽 Link inputs UNDER the buttons */}
            {newFileType === 'link' && (
              <>
                <TextField
                  fullWidth
                  variant="outlined"
                  margin="dense"
                  label={t("Link name")}
                  value={linkName}
                  onChange={(e) => setLinkName(e.target.value)}
                  sx={{ direction: 'rtl', mb: 1 }}
                />
                <TextField
                  fullWidth
                  variant="outlined"
                  margin="dense"
                  label={t("Enter the link here")}
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  sx={{ direction: 'rtl', mb: 2 }}
                />
              </>
            )}

            {/* File / video / audio / image selection + progress */}
            {(newFileType === 'file' || newFileType === 'video' || newFileType === 'audio' || newFileType === 'image' || newFileType === 'swf') && (
              <>
                {newFileType === 'file' && newFile && (
                  <Typography sx={{ fontSize: '13px' }}>{newFile.name}</Typography>
                )}

                {newFileType === 'video' && video && (
                  <Typography sx={{ fontSize: '13px' }}>{video.name}</Typography>
                )}

                {newFileType === 'audio' && audio && (
                  <Typography sx={{ fontSize: '13px' }}>{audio.name}</Typography>
                )}

                {newFileType === 'image' && image && (
                  <Typography sx={{ fontSize: '13px' }}>{image.name}</Typography>
                )}

                {newFileType === 'swf' && swf && (
                  <Typography sx={{ fontSize: '13px' }}>{swf.name}</Typography>
                )}

                {newFileType === 'file' && progress > 0 && (
                  <LinearProgress variant="determinate" value={progress} />
                )}

                {(newFileType === 'video' || newFileType === 'audio' || newFileType === 'image') && isUploading && (
                  <LinearProgress variant="indeterminate" />
                )}
              </>
            )}
          </>
        )}
      </DialogContent>


      {error && (
        <Typography
          sx={{ display: 'flex', justifyContent: 'center', color: 'red' }}
        >
          {error}
        </Typography>
      )}
      <DialogActions sx={{ display: 'flex', flexDirection: 'column', gap: 1, px: 3, pb: 2, pt: 1 }}>
        {activeStep < 1 ? (
          <Button onClick={handleNext} variant="contained" color="primary" fullWidth sx={{ fontSize: '16px' }}>
            {t("Next")} <KeyboardDoubleArrowRightIcon />
          </Button>
        ) : (
          <Button
            onClick={uploadFile}
            variant="contained"
            color="primary"
            disabled={isUploading}
            fullWidth
            sx={{ fontSize: '16px' }}
          >
            {t("Save")}
          </Button>
        )}
        {activeStep > 0 && (
          <Button onClick={handleBack} variant="outlined" color="primary" fullWidth sx={{ fontSize: '16px' }}>
            <KeyboardDoubleArrowLeftIcon /> {t("Back")}
          </Button>
        )}
      </DialogActions>
    </Dialog>

    {/* Snackbar for "Coming Soon" message */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
       >
       <Alert onClose={handleClose} severity="info" sx={{ width: '100%' }}>
          {t("Please contact the administration to enable this feature.")}
       </Alert>
     </Snackbar>
 </div>
  );
}

export default TeacherAddLesson;
